export const PRIORITY_COMPANIES = [
  'Adani Group',
  'Reliance Industries',
  'Tata Sons',
  'Vedanta',
  'JSW Group',
];

// Known company → domain mapping for Clearbit logo lookup.
// Falls back to initials avatar when domain is unknown or image fails to load.
const COMPANY_DOMAINS = {
  'Adani Group': 'adani.com',
  'Reliance Industries': 'ril.com',
  'Vedanta': 'vedantalimited.com',
  'Vedanta Limited': 'vedantalimited.com',
  'Vedanta Resources': 'vedantalimited.com',
  'Tata Group': 'tata.com',
  'Tata': 'tata.com',
  'JSW Group': 'jsw.in',
  'Tata Sons': 'tata.com',
  'Flipkart': 'flipkart.com',
  'Byju\'s': 'byjus.com',
  'Meesho': 'meesho.com',
  'Makemytrip': 'makemytrip.com',
  'Goldman Sachs': 'goldmansachs.com',
  'Bain Capital': 'baincapital.com',
  'Brookfield Asset Management': 'brookfield.com',
  'WPP': 'wpp.com',
  'AkzoNobel N.V.': 'akzonobel.com',
  'Apollo': 'apollohospitals.com',
  'Piramal Enterprises Limited': 'piramal.com',
  'Glenmark Life Sciences': 'glenmarkpharma.com',
  'Nirma Limited': 'nirma.co.in',
  'Quess Corp Limited': 'quesscorp.com',
  'Nuvama Wealth Management': 'nuvama.com',
  'PayU': 'payu.com',
  'BillDesk': 'billdesk.com',
  'Elastic Run': 'elastic.run',
  'Fabhotels': 'fabhotels.com',
  'Onsitego': 'onsitego.com',
  'Peugeot S.A. (Stellantis)': 'stellantis.com',
  'CK Birla Group': 'ckbirlagroup.com',
  'Reliance Communications': 'rcom.co.in',
  'AWL Agri Business Limited': 'adaniwilmar.com',
  'AK Capital Services': 'akcapitalservices.com',
  'China Development Bank': 'cdb.com.cn',
  'Export Import Bank of China': 'eximbank.gov.cn',
  'Industrial and Commercial Bank of China': 'icbc.com.cn',
  'National Investment and Infrastructure Fund': 'niifindia.in',
  'BEL': 'bel-india.in',
  'Bharat Electronics': 'bel-india.in',
  'Kantar': 'kantar.com',
  'Naspers Group': 'naspers.com',
  'Prosus Ventures': 'prosus.com',
  'PSP Investments': 'investpsp.com',
};

const DOMAIN_STOP_WORDS = new Set([
  'limited', 'ltd', 'pvt', 'private', 'inc', 'incorporated', 'corp', 'corporation',
  'company', 'co', 'group', 'holdings', 'holding', 'the', 'and', 'of', 'india',
  'industries', 'enterprise', 'enterprises', 'services', 'solutions', 'technologies',
  'technology', 'international', 'global', 'bank', 'banks',
]);

/** Guess a public website domain so unknown companies still get a logo. */
function guessDomainFromName(normalized) {
  const words = normalized
    .replace(/[.'()&,]/g, ' ')
    .split(/\s+/)
    .filter(w => w.length >= 2 && !DOMAIN_STOP_WORDS.has(w));

  const slug = (words[0] || normalized.split(/\s+/)[0] || '')
    .replace(/[^a-z0-9]/g, '');

  if (slug.length < 2) return null;
  return `${slug}.com`;
}

export function getCompanyDomain(name) {
  if (!name) return null;
  if (COMPANY_DOMAINS[name]) return COMPANY_DOMAINS[name];

  const normalized = name.trim().toLowerCase();
  const exact = Object.entries(COMPANY_DOMAINS).find(
    ([key]) => key.toLowerCase() === normalized,
  );
  if (exact) return exact[1];

  // Whole-word matches only: a plain substring test matched "bel" inside "akzonobel".
  const wordMatch = (haystack, needle) => {
    const escaped = needle.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    return new RegExp(`(^|[^a-z0-9])${escaped}($|[^a-z0-9])`).test(haystack);
  };
  const partial = Object.entries(COMPANY_DOMAINS).find(([key]) => {
    const k = key.toLowerCase();
    return wordMatch(normalized, k) || (normalized.length >= 4 && wordMatch(k, normalized));
  });
  if (partial) return partial[1];

  const firstWord = normalized.split(/\s+/)[0];
  if (firstWord.length >= 4) {
    const byWord = Object.entries(COMPANY_DOMAINS).find(([key]) => (
      key.toLowerCase().startsWith(firstWord)
      || firstWord.startsWith(key.toLowerCase().split(/\s+/)[0])
    ));
    if (byWord) return byWord[1];
  }

  // New / unmapped companies: still try favicon/logo from a guessed domain
  return guessDomainFromName(normalized);
}

export function getCompanyInitials(name) {
  if (!name) return '?';
  const words = name.replace(/[.'()]/g, '').split(/\s+/).filter(Boolean);
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + (words[1]?.[0] || '')).toUpperCase();
}